/**
 * rig_normals_apply — smooth every mesh of a Meshy body (kind 'avatar': the versona rigs, their static twins, the
 * scanned player bodies, the crew figures) across its UV seams at parse, once per cached scene (shader gaps,
 * coordinator, 2026-09-30). See `lib/rig_normals.ts` for why: the split normals drew every face as flat patches.
 *
 * Off the main thread (`workers/rig_normals_worker.ts`), the synchronous function as the fallback when the worker is
 * missing, broken or silent (THE STALL law: a quiet worker is dead, never awaited forever). Only the `normal`
 * attribute is replaced (with a Float32 attribute — a quantised normal cannot hold the smoothed direction); index,
 * UVs and skin attributes are untouched, so a SkinnedMesh keeps binding. Kill switch: localStorage
 * `atlas:rig-normals` = 'off'. Lever: `window.__rigNormals()`.
 */
import * as THREE from 'three/webgpu';
import { weldSplitNormals } from './rig_normals.js';
import type { RigNormalsReply } from '../workers/rig_normals_worker.js';

/** The crease: split normals closer than this at one position are one surface (a Meshy chart seam). */
export const RIG_NORMAL_CREASE_DEG = 60;
const QUIET_LIMIT_MS = 20_000;
const BOOT_LIMIT_MS = 60_000;

export const rigNormalStats = { meshes: 0, vertices: 0, changed: 0, workerMs: 0, fallbacks: 0, skipped: 0, lastError: '' };

let worker: Worker | null = null;
let workerBroken = false;
let everReplied = false;
let nextId = 1;
const pending = new Map<number, (r: RigNormalsReply) => void>();

function fail(error: string): void {
  workerBroken = true;
  rigNormalStats.lastError = error;
  worker?.terminate();
  worker = null;
  for (const [, res] of pending) res({ id: -1, error });
  pending.clear();
}

function getWorker(): Worker | null {
  if (workerBroken || typeof Worker === 'undefined') return null;
  if (!worker) {
    try {
      worker = new Worker(new URL('../workers/rig_normals_worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (ev: MessageEvent<RigNormalsReply>) => {
        everReplied = true;
        const res = pending.get(ev.data.id);
        if (res) { pending.delete(ev.data.id); res(ev.data); }
      };
      worker.onerror = () => fail('worker crashed');
    } catch { workerBroken = true; return null; }
  }
  return worker;
}

export function rigNormalsEnabled(): boolean {
  try { return localStorage.getItem('atlas:rig-normals') !== 'off'; } catch { return true; }
}

async function weldOne(mesh: THREE.Mesh): Promise<void> {
  const geo = mesh.geometry;
  const pa = geo?.getAttribute('position'), na = geo?.getAttribute('normal');
  if (!pa || !na || pa.count !== na.count) { rigNormalStats.skipped++; return; }
  const n = pa.count;
  const P = new Float32Array(n * 3), N = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    P[i * 3] = pa.getX(i); P[i * 3 + 1] = pa.getY(i); P[i * 3 + 2] = pa.getZ(i);
    N[i * 3] = na.getX(i); N[i * 3 + 1] = na.getY(i); N[i * 3 + 2] = na.getZ(i);
  }
  let out: Float32Array | null = null;
  let changed = 0;
  const w = getWorker();
  if (w) {
    const id = nextId++;
    const reply = await new Promise<RigNormalsReply>((resolve) => {
      const sentAt = performance.now();
      const check = (): void => {
        if (!pending.has(id)) return;
        const limit = everReplied ? QUIET_LIMIT_MS : BOOT_LIMIT_MS;
        if (performance.now() - sentAt < limit) { setTimeout(check, 1000); return; }
        fail('worker stalled');
      };
      setTimeout(check, 1000);
      pending.set(id, resolve);
      w.postMessage({ id, positions: P, normals: N, vertexCount: n, creaseDeg: RIG_NORMAL_CREASE_DEG }, [P.buffer, N.buffer]);
    });
    if (reply.normals) { out = reply.normals; changed = reply.changed ?? 0; rigNormalStats.workerMs += reply.ms ?? 0; }
  }
  if (!out) {
    // the fallback reads the attributes again (the transfer detached the copies)
    const P2 = new Float32Array(n * 3), N2 = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      P2[i * 3] = pa.getX(i); P2[i * 3 + 1] = pa.getY(i); P2[i * 3 + 2] = pa.getZ(i);
      N2[i * 3] = na.getX(i); N2[i * 3 + 1] = na.getY(i); N2[i * 3 + 2] = na.getZ(i);
    }
    const r = weldSplitNormals(P2, N2, n, RIG_NORMAL_CREASE_DEG);
    out = r.normals; changed = r.changed;
    rigNormalStats.fallbacks++;
  }
  geo.setAttribute('normal', new THREE.BufferAttribute(out, 3));
  rigNormalStats.meshes++;
  rigNormalStats.vertices += n;
  rigNormalStats.changed += changed;
}

/** Smooth the split normals of every opaque mesh of a body scene. Idempotent per geometry. */
export async function weldBodyNormals(root: THREE.Object3D): Promise<void> {
  if (!rigNormalsEnabled()) return;
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry || (m.geometry.userData as { rigNormalsWelded?: boolean }).rigNormalsWelded) return;
    meshes.push(m);
  });
  for (const m of meshes) {
    if ((m.geometry.userData as { rigNormalsWelded?: boolean }).rigNormalsWelded) continue; // shared by two meshes
    try { await weldOne(m); } catch (e) { rigNormalStats.lastError = String(e).slice(0, 200); }
    (m.geometry.userData as { rigNormalsWelded?: boolean }).rigNormalsWelded = true;
  }
}

if (typeof window !== 'undefined') (window as unknown as { __rigNormals?: () => unknown }).__rigNormals = () => ({ enabled: rigNormalsEnabled(), crease: RIG_NORMAL_CREASE_DEG, workerBroken, ...rigNormalStats });
